import { useContext, useMemo, useState } from 'react';
import { GlobalContext } from './GlobalContext';
import { canSeeTab } from './tabPermissions';

/**
 * The app overview: one pass over the header and the tab strip, for someone
 * seeing the app for the first time.
 *
 * This used to live inline in Cars.jsx, started only by clicking the company
 * logo, and it listed every tab unconditionally — so for anyone without
 * payments.view or users.manage it walked into a target that does not exist,
 * with no error branch and no controlled stepIndex to recover with. The tour
 * simply stopped. The step list is now built from the same permission gate the
 * tab strip uses, and target_not_found advances instead of stalling.
 */

//The words for each tab, and NOTHING about who may see it. These entries used
//to carry their own `perm:` values — a fourth copy of the tab gating, alongside
//NavTabs, tourRegistry and tabPermissions.js, the last of which exists
//specifically to stop that. It was already stale: it had Settings gated on
//users.manage, so a General Manager who could open the tab would have had its
//step silently skipped. Gating comes from canSeeTab; only the copy lives here.
//
//Kept as a separate list from the header steps because only these need filtering.
const TAB_STEPS = [
    {
        id: 'tab-dashboard',
        title: 'Dashboard',
        content: `This is your dashboard, where you get an overview of your dealership's performance.`,
    },
    {
        id: 'tab-inventory',
        title: 'Inventory',
        content: 'Add and keep an eye on your vehicle inventory and stock levels here.',
    },
    {
        id: 'tab-sales',
        title: 'Sales',
        content: 'Committed, cash and credit deals all live here, from the sale through to the last payment.',
    },
    {
        id: 'tab-customers',
        title: 'Customers',
        content: 'View and manage your customer database in this tab.',
    },
    {
        id: 'tab-brokers',
        title: 'Brokers',
        content: 'Manage relationships with brokers and intermediaries here.',
    },
    {
        id: 'tab-sellers',
        title: 'Sellers',
        content: 'Oversee your sellers for locally used vehicles in this section.',
    },
    {
        id: 'tab-messages',
        title: 'Messages',
        content: 'Send an SMS to a whole group of customers, sellers or brokers at once, and check afterwards who actually received it.',
    },
    {
        id: 'tab-enquiries',
        title: 'Enquiries',
        content: 'Leads from the contact form on your website land here, so one that never reached your inbox is still not lost.',
    },
    {
        id: 'tab-payments',
        title: 'Payments',
        content: 'Get a comprehensive view of all payment transactions and histories here.',
    },
    {
        id: 'tab-reports',
        title: 'Reports',
        content: 'Generate and view detailed reports on various aspects of your dealership.',
    },
    {
        id: 'tab-settings',
        title: 'Settings',
        content: 'Your company details, user accounts, subscription and credits are all set up in this tab.',
    },
    {
        id: 'tab-recyclebin',
        title: 'Recycle Bin',
        content: 'Deleted records land here first rather than vanishing, so a mistake can be undone.',
    },
];

function useGlobalTour() {
    const { can } = useContext(GlobalContext);
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const steps = useMemo(() => [
        {
            target: '#global-search',
            title: 'Find any vehicle from anywhere',
            content: 'Type a number plate, chassis number, make, model, year or colour and this searches every sheet at once — including the sold and cancelled ones, whose older records no single sheet\'s own search box can reach. Pick a result and the vehicle opens.',
            disableBeacon: true,
        },
        {
            target: '#profile-button',
            title: 'Profile Menu',
            content: 'Your profile, your password, your passkeys and your email notifications all live behind here.',
            disableBeacon: true,
        },
        //A tab can also be missing because it collapsed into the More menu, but
        //the strip renders everything while a tour runs (NavTabs.jsx), so
        //permission is the only filter that matters here.
        ...TAB_STEPS
            .filter(tab => canSeeTab(tab.id, can))
            .map(({ id, title, content }) => ({ target: `#${id}`, title, content })),
        {
            target: '#help-menu-button',
            title: 'Need help again?',
            content: 'Most screens have a tour of their own. Click this question mark at any time to start the one for the screen you are on.',
            placement: 'bottom',
        },
    ], [can]);

    const handleStartTutorial = () => {
        setRunTour(false);
        setStepIndex(0);
        setTimeout(() => {
            setStepIndex(0);
            setRunTour(true);
        }, 100);
    };

    //Deliberately never dereferences steps[index]: the old version did, and an
    //out-of-range index during an error callback threw rather than recovering.
    const handleJoyrideCallback = (data) => {
        const { status, action, type, index, origin } = data;

        if (origin === 'overlay' && action === 'close') return;

        if (type === 'error:target_not_found' || status === 'error') {
            if (index + 1 < steps.length) {
                setStepIndex(index + 1);
                return;
            }
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (status === 'finished' || status === 'skipped' || action === 'close' || action === 'stop') {
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (type === 'step:after' && action === 'next') {
            setStepIndex(index + 1);
        } else if (type === 'step:after' && action === 'prev') {
            if (index - 1 < 0) {
                setRunTour(false);
                setStepIndex(0);
                return;
            }
            setStepIndex(index - 1);
        }
    };

    return { runTour, stepIndex, steps, handleStartTutorial, handleJoyrideCallback };
}

export default useGlobalTour;
